import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";

const NAV = [
  { label: "Služby", to: "/#services" },
  { label: "Proces", to: "/#process" },
  { label: "Reference", to: "/#reference" },
  { label: "Kontakt", to: "/#contact" },
];

// Click handler that smooth-scrolls to a target id when we're already on
// the homepage. Prevents React Router from no-op'ing on same-path hash
// changes; falls back to <Link> navigation when on another route.
const useScrollToHash = () => {
  const location = useLocation();
  return (hashId) => (e) => {
    if (location.pathname === "/") {
      const el = document.getElementById(hashId);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, "", `/#${hashId}`);
        }
      }
    }
  };
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const scrollToHash = useScrollToHash();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On legal pages (light bg, scrolled top), always use dark text mode.
  const onDark = isHome && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        !onDark
          ? "bg-white/85 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-16 md:h-[88px]">
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center group leading-none"
            aria-label="SANVIORO"
          >
            {/* Mobile: just the teal S — unchanged */}
            <img
              src="/sanvioro-icon-s.png"
              alt=""
              aria-hidden="true"
              className="md:hidden h-11 w-auto block transition-transform duration-300 group-hover:rotate-[-8deg]"
            />
            {/* Desktop: full horizontal wordmark — +25% vs previous size */}
            <img
              src={
                onDark
                  ? "/sanvioro-logo-wordmark-white.png"
                  : "/sanvioro-logo-wordmark.png"
              }
              alt="SANVIORO"
              className="hidden md:block h-[55px] lg:h-[60px] w-auto block"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => {
              const id = n.to.replace("/#", "");
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={scrollToHash(id)}
                  data-testid={`nav-${id}`}
                  className={`text-sm font-medium transition-colors ${
                    onDark
                      ? "text-white/85 hover:text-white"
                      : "text-[#111111]/80 hover:text-[#0F5C69]"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href="tel:+420775601254"
              data-testid="header-phone"
              className={`hidden sm:block text-sm font-semibold tabular-nums transition-colors ${
                onDark ? "text-white" : "text-[#111111]"
              }`}
            >
              +420 775 601 254
            </a>
            <Link
              to="/#calc"
              data-testid="header-cta"
              onClick={(e) => {
                trackEvent("cta_click_spocitat_uklid", { location: "header" });
                scrollToHash("calc")(e);
              }}
              className={`inline-flex items-center px-4 md:px-5 h-10 text-sm font-semibold tracking-tight transition-colors ${
                onDark
                  ? "bg-white text-[#0F5C69] hover:bg-[#E6F2F4]"
                  : "bg-[#0F5C69] hover:bg-[#0B4B55] text-white"
              }`}
            >
              Spočítat úklid
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className={`md:hidden w-10 h-10 inline-flex items-center justify-center border ${
                onDark ? "border-white/30" : "border-black/10"
              }`}
              aria-label="Menu"
            >
              <span
                className={`block w-4 h-[2px] relative before:content-[''] before:absolute before:-top-[6px] before:left-0 before:w-4 before:h-[2px] after:content-[''] after:absolute after:top-[6px] after:left-0 after:w-4 after:h-[2px] ${
                  onDark
                    ? "bg-white before:bg-white after:bg-white"
                    : "bg-black before:bg-black after:bg-black"
                }`}
              />
            </button>
          </div>
        </div>

        {open && (
          <div
            className={`md:hidden pb-4 flex flex-col gap-3 ${
              onDark ? "bg-black/60 backdrop-blur-xl -mx-6 px-6 pt-3" : ""
            }`}
            data-testid="mobile-menu"
          >
            {NAV.map((n) => {
              const id = n.to.replace("/#", "");
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={(e) => {
                    setOpen(false);
                    scrollToHash(id)(e);
                  }}
                  className={`text-sm font-medium py-1 ${
                    onDark ? "text-white/90" : "text-[#111111]/80"
                  }`}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
